import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, MapPin, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canManage } from "@/contexts/AuthContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import GeoPicker from "@/components/GeoPicker";

const TOUR_STORAGE_KEY = "chkr_tour_centros_seen";

// Lista completa de zonas horarias IANA del navegador; con respaldo curado
// (LatAm + EE. UU.) para navegadores sin Intl.supportedValuesOf.
const TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch { /* noop */ }
  return [
    "America/Mexico_City", "America/Cancun", "America/Merida", "America/Monterrey",
    "America/Matamoros", "America/Mazatlan", "America/Chihuahua", "America/Ciudad_Juarez",
    "America/Ojinaga", "America/Hermosillo", "America/Tijuana", "America/Bahia_Banderas",
    "America/Bogota", "America/Lima", "America/Santiago", "America/Guatemala",
    "America/Argentina/Buenos_Aires", "America/Sao_Paulo", "America/New_York",
    "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "UTC",
  ];
})();

function tourSteps(user) {
  const steps = [];
  if (canManage(user)) {
    steps.push({ target: "[data-tour='centros-nueva']", title: "Registrar un centro", content: "Da de alta sucursales, oficinas o almacenes: domicilio, modalidad, zona horaria y el radio de geocerca usado para validar el check-in." });
  }
  steps.push({ target: "[data-tour='centros-tabla']", title: "Centros de trabajo", content: "Cada centro pertenece a una empresa y define dónde deben checar sus empleados." });
  return steps;
}

export default function Centros() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    empresa_id: "", nombre: "", domicilio: "", responsable: "",
    area: "", zona_horaria: "America/Mexico_City", modalidad: "presencial",
    geo_lat: null, geo_lng: null, geo_radio_metros: 200,
  });
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [mapFocus, setMapFocus] = useState(0);  // bump => el mapa vuela al punto
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  // Geocodifica el domicilio vía el backend (Google Geocoding API — ver
  // /centros/geocodificar) y coloca el punto. La API key de Google nunca llega al
  // navegador; el backend es quien la usa.
  const buscarEnMapa = async () => {
    const q = (form.domicilio || "").trim();
    if (!q) return;
    setGeoBusy(true);
    try {
      const { data } = await api.get("/centros/geocodificar", { params: { direccion: q } });
      setForm((f) => ({ ...f, geo_lat: data.lat, geo_lng: data.lng }));
      setMapFocus((n) => n + 1);
      toast.success("Ubicación encontrada. Ajusta el punto en el mapa si hace falta.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo buscar la dirección. Coloca el punto manualmente en el mapa.");
    } finally {
      setGeoBusy(false);
    }
  };

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/centros"), api.get("/empresas")]);
    setItems(c.data);
    setEmpresas(e.data);
  };
  useEffect(() => { load(); }, []);

  const empresaName = (id) => empresas.find((e) => e.id === id)?.razon_social || "—";

  const startCreate = () => {
    setEditing(null);
    setForm({
      empresa_id: empresas[0]?.id || "", nombre: "", domicilio: "", responsable: "",
      area: "", zona_horaria: "America/Mexico_City", modalidad: "presencial",
      geo_lat: null, geo_lng: null, geo_radio_metros: 200,
    });
    setOpen(true);
  };
  const startEdit = (c) => {
    setEditing(c); setForm(c); setOpen(true);
    if (c.geo_lat != null) setMapFocus((n) => n + 1);  // centra el mapa en el punto guardado
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/centros/${editing.id}`, form);
        toast.success("Centro actualizado");
      } else {
        await api.post("/centros", form);
        toast.success("Centro creado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="centros-page">
      <PageHeader
        kicker="Catálogos"
        title="Centros de Trabajo"
        subtitle="Sucursales, oficinas y almacenes asociados a cada empresa."
        actions={
          <div className="flex gap-2 items-center">
            {canManage(user) && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="centro-new-btn" data-tour="centros-nueva">
                <Plus size={16} weight="bold" className="mr-2" /> Nuevo centro
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="centros-tabla">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Centro</th>
              <th>Empresa</th>
              <th>Responsable</th>
              <th>Modalidad</th>
              <th>Zona horaria</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">Sin centros</td></tr>}
            {items.map((c) => (
              <tr key={c.id} data-testid={`centro-row-${c.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center text-slate-700">
                      <MapPin size={18} weight="duotone" />
                    </div>
                    <div>
                      <div className="font-semibold text-ink">{c.nombre}</div>
                      <div className="text-xs text-slate-500">{c.domicilio}</div>
                    </div>
                  </div>
                </td>
                <td className="text-sm">{empresaName(c.empresa_id)}</td>
                <td>{c.responsable}</td>
                <td>
                  <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 capitalize">{c.modalidad}</span>
                </td>
                <td className="font-mono text-xs">{c.zona_horaria}</td>
                <td>
                  {canManage(user) && (
                    <Button variant="ghost" size="sm" onClick={() => startEdit(c)}>
                      <PencilSimple size={16} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="centro-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar centro" : "Nuevo centro"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2 space-y-1">
              <Label className="label-uppercase">Empresa</Label>
              <Select value={form.empresa_id} onValueChange={(v) => setForm({ ...form, empresa_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona empresa" /></SelectTrigger>
                <SelectContent>
                  {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="centro-nombre-input" />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Responsable</Label>
              <Input value={form.responsable || ""} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-1">
              <Label className="label-uppercase">Domicilio</Label>
              <div className="flex gap-2">
                <Input
                  className="flex-1"
                  value={form.domicilio || ""}
                  onChange={(e) => setForm({ ...form, domicilio: e.target.value })}
                  placeholder="Calle, número, colonia, ciudad, estado"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={buscarEnMapa}
                  disabled={geoBusy || !(form.domicilio || "").trim()}
                  data-testid="centro-geocode-btn"
                >
                  <MapPin size={16} weight="bold" className="mr-1.5" />
                  {geoBusy ? "Buscando…" : "Ubicar en mapa"}
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Área</Label>
              <Input value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Modalidad</Label>
              <Select value={form.modalidad} onValueChange={(v) => setForm({ ...form, modalidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="teletrabajo">Teletrabajo</SelectItem>
                  <SelectItem value="hibrida">Híbrida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Zona horaria</Label>
              <Input
                list="tz-options"
                value={form.zona_horaria}
                onChange={(e) => setForm({ ...form, zona_horaria: e.target.value })}
                placeholder="America/Mexico_City"
              />
              <datalist id="tz-options">
                {TIMEZONES.map((tz) => <option key={tz} value={tz} />)}
              </datalist>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Radio Geo (m)</Label>
              <Input type="number" value={form.geo_radio_metros} onChange={(e) => setForm({ ...form, geo_radio_metros: parseInt(e.target.value || "0") })} />
            </div>

            <div className="col-span-1 sm:col-span-2 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label className="label-uppercase">Ubicación de la geocerca</Label>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {form.geo_lat != null && form.geo_lng != null ? (
                    <span className="font-mono">
                      {Number(form.geo_lat).toFixed(6)}, {Number(form.geo_lng).toFixed(6)}
                    </span>
                  ) : (
                    <span>Haz clic en el mapa para fijar el punto</span>
                  )}
                  {form.geo_lat != null && (
                    <button
                      type="button"
                      className="underline hover:text-slate-700"
                      onClick={() => setForm({ ...form, geo_lat: null, geo_lng: null })}
                    >
                      Quitar punto
                    </button>
                  )}
                </div>
              </div>
              <GeoPicker
                lat={form.geo_lat ?? null}
                lng={form.geo_lng ?? null}
                radio={form.geo_radio_metros}
                focusSignal={mapFocus}
                onChange={({ lat, lng }) => setForm((f) => ({ ...f, geo_lat: lat, geo_lng: lng }))}
              />
              <p className="text-xs text-slate-400">
                Escribe el domicilio y usa “Ubicar en mapa”, o haz clic directamente en el mapa.
                El círculo punteado es el radio de {form.geo_radio_metros || 0} m dentro del cual se aceptan los check-ins.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="centro-save-btn">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
